import type { IconName } from '@/components/ui/Icon/Icon';
import type { RiskLevel, SiteAnalysisResponse } from '@/lib/api.types';

// 章の識別子。DOM の anchor id (#hazards など) と章ナビ・章扉の照合に使う。
// 順序を含意しない名前にしてあるので、章を並べ替えても id は変わらない。
export type ChapterId =
  | 'overview'
  | 'hazards'
  | 'ground'
  | 'building'
  | 'price'
  | 'environment'
  | 'prospects'
  | 'summary';

export type ChapterMeta = {
  id: ChapterId;
  title: string;
  shortTitle: string;
  icon: IconName;
};

// 章の並び = この配列の順。番号はここから導出する(各章コンポーネントは番号を持たない)。
// 読み手の物語:
//   1 あらまし ── この土地はどこで、どんな街か
//   2 災害 / 3 地盤 ── 土地そのものの安全(本サービスの主柱なので先頭に連続で置く。
//     総括カードの観点順 災害→地盤→建築→暮らし とも揃える)
//   4 建築 ── その土地に何が建てられるか(造成→建築のつながり)
//   5 価格 ── 条件が分かったうえで値段は妥当か
//   6 暮らし / 7 見通し ── いまの暮らし → これから(同じ life_environment を共有。
//     見通しはデータが無いと出ないため、番号が詰まる位置として末尾側に置く)
//   8 まとめ
export const CHAPTERS: readonly ChapterMeta[] = [
  { id: 'overview', title: 'この土地のあらまし', shortTitle: 'あらまし', icon: 'map-pin' },
  { id: 'hazards', title: '災害リスクのところ', shortTitle: '災害', icon: 'droplet' },
  { id: 'ground', title: '地盤と造成のところ', shortTitle: '地盤', icon: 'layers-subtract' },
  { id: 'building', title: '建てられる家のめやす', shortTitle: '建築', icon: 'home' },
  { id: 'price', title: '価格相場のみたて', shortTitle: '価格', icon: 'currency-yen' },
  { id: 'environment', title: '暮らしの環境', shortTitle: '暮らし', icon: 'school' },
  { id: 'prospects', title: 'これからの見通し', shortTitle: '見通し', icon: 'chart-line' },
  { id: 'summary', title: 'まとめと注意ごと', shortTitle: 'まとめ', icon: 'notebook' },
] as const;

export function chapterMeta(id: ChapterId): ChapterMeta {
  const meta = CHAPTERS.find((c) => c.id === id);
  if (!meta) throw new Error(`unknown chapter id: ${id}`);
  return meta;
}

// life_environment エンドポイントの振り分け（コードは backend の LAYER_CONFIGS と一致させること）。
// 学区 / 学校 / 保育 / 医療 / 福祉 / 駅 / 図書館 / 役場 / 避難場所 → 暮らし章 (暮らしの環境)。
// これらは不動産情報ライブラリの実 endpoint コード。以前は旧コード(XKT001-010の連番)を
// 仮置きしていたため、駅(XKT015)が暮らし章・見通し章どちらにも振り分けられず地図に出ていなかった。
export const LIFE_AMENITY_ENDPOINTS = new Set([
  'XKT004', // 小学校区
  'XKT005', // 中学校区
  'XKT006', // 学校
  'XKT007', // 保育園・幼稚園等
  'XKT010', // 医療機関
  'XKT011', // 福祉施設 (full)
  'XKT015', // 駅別乗降客数
  'XKT017', // 図書館 (full)
  'XKT018', // 市区町村役場・集会施設等 (full)
  'XGT001', // 指定緊急避難場所 (full)
]);

// 人口集中地区 / 将来推計人口 → 見通し章 (これからの見通し)
export const LIFE_FUTURE_ENDPOINTS = new Set([
  'XKT013', // 将来推計人口250mメッシュ (full)
  'XKT031', // 人口集中地区 (DID)
]);

// 見通し章は人口集中地区/将来推計のデータがある時だけ出す。データが無いと「準備中」のスタブが
// 常時表示され、購入者には未完成に映るため。
export function hasProspectsData(analysis: SiteAnalysisResponse): boolean {
  return analysis.life_environment.layers.some((l) => LIFE_FUTURE_ENDPOINTS.has(l.endpoint));
}

// その回のレポートで実際に出す章の並び (番号はこの並びの位置)。画面 (Shirabe / ShirabeSample) と
// PDF 冊子が同じ関数で章集合を決めるので、番号や目次がずれない。
export function visibleChaptersFor(analysis: SiteAnalysisResponse | null): readonly ChapterMeta[] {
  if (analysis && hasProspectsData(analysis)) return CHAPTERS;
  return CHAPTERS.filter((c) => c.id !== 'prospects');
}

// Tabler icon hints for layer endpoints (hazard chapter)
export const HAZARD_ICON_FOR: Record<string, IconName> = {
  XKT026: 'droplet',
  XKT027: 'wave-sine',
  XKT028: 'wave-sine',
  XKT029: 'mountain',
  XKT030: 'mountain',
  XKT031: 'mountain',
  XKT032: 'ripple',
};

// Hazard endpoint → sub-map group (災害章 splits maps by family)
export type HazardGroup = 'flood' | 'landslide';

export const HAZARD_GROUP_FOR_ENDPOINT: Record<string, HazardGroup> = {
  XKT026: 'flood',
  XKT027: 'flood',
  XKT028: 'flood',
  XKT032: 'flood',
  XKT029: 'landslide',
  XKT030: 'landslide',
  XKT031: 'landslide',
};

export const LIFE_ICON_FOR: Record<string, IconName> = {
  XKT004: 'school',             // 小学校区
  XKT005: 'school',             // 中学校区
  XKT006: 'school',             // 学校
  XKT007: 'school',             // 保育園・幼稚園等
  XKT010: 'stethoscope',        // 医療機関
  XKT011: 'building-community',  // 福祉施設
  XKT013: 'chart-line',         // 将来推計人口250mメッシュ
  XKT015: 'train',              // 駅別乗降客数
  XKT017: 'building-community',  // 図書館
  XKT018: 'building-community',  // 市区町村役場・集会施設等
  XKT031: 'users-group',        // 人口集中地区 (DID)
  XGT001: 'shield-check',       // 指定緊急避難場所
};

export const PLANNING_ICON_FOR: Record<string, IconName> = {
  XKT002: 'building-community',
  XKT014: 'shield-check',
  XKT015: 'map-2',
};

// Hazard polygon fill colors by risk rank — for 災害章 map overlays.
export const HAZARD_COLOR_FOR_RANK: Record<RiskLevel, string> = {
  very_high: '#C76D5A',
  high: '#D9805F',
  medium: '#D9A441',
  low: '#92B662',
  unknown: '#A8A8A8',
};

// J-SHIS 表層地盤 ARV（揺れやすさ）ランク → 色・ラベル。地盤章の J-SHIS メッシュ heatmap 用。
export type ArvRank = 'A' | 'B' | 'C' | 'D' | 'E' | 'Unknown';

export const ARV_RANK_COLOR: Record<ArvRank, string> = {
  A: '#4F8A53', // 揺れにくい
  B: '#92B662',
  C: '#D9A441',
  D: '#D9805F',
  E: '#C76D5A', // 特に揺れやすい
  Unknown: '#A8A8A8',
};

export const ARV_RANK_LABEL: Record<ArvRank, string> = {
  A: '揺れにくい',
  B: '標準〜やや揺れにくい',
  C: 'やや揺れやすい',
  D: '揺れやすい',
  E: '特に揺れやすい',
  Unknown: '判定不能',
};

// 水害ハザード（洪水・高潮・津波）— 災害章で浸水深ヒートマップ表示する対象。
// 内水(XKT032)は不動産情報ライブラリにエンドポイントが無いため対象外。
export const WATER_HAZARD_ENDPOINTS = new Set(['XKT026', 'XKT027', 'XKT028']);

// 浸水深ランク(1-6) → 公式配色（国交省 重ねるハザードマップ「現在の凡例」）。
// 洪水(想定最大規模)・津波・高潮で共通の浸水深凡例。
export const FLOOD_DEPTH_COLOR: Record<number, string> = {
  1: '#F7F5A9', // 〜0.5m
  2: '#FFD8C0', // 0.5〜3m
  3: '#FFB7B7', // 3〜5m
  4: '#FF9191', // 5〜10m
  5: '#F285C9', // 10〜20m
  6: '#DC7ADC', // 20m〜
};

export const FLOOD_DEPTH_LABEL: Record<number, string> = {
  1: '0.5m未満',
  2: '0.5〜3m',
  3: '3〜5m',
  4: '5〜10m',
  5: '10〜20m',
  6: '20m以上',
};

// 価格ヒートマップ（取引価格・地価公示）— 価格章の地図で坪単価を色分けする。
// 周辺内の相対レンジ（最小=安→最大=高）で寒色→暖色に割り当てるため、地域の絶対水準に
// 依らず「近隣のどこが相対的に高い/安いか」が読み取れる。取引(XPT001)と地価公示(XPT002)は
// バックエンドで共通の坪単価(円)へ正規化済みなので、同じランプで直接比較できる。
export const PRICE_HEAT_COLORS = ['#4E7FA8', '#86B0C7', '#E4C56A', '#DC8C5B', '#C25C4E'] as const;

// 単価が取得できなかった点（値が解析不能）の中立色。
export const PRICE_NEUTRAL_COLOR = '#A8A8A8';

// 単価(円。坪でも ㎡ でも換算率は一定なので順位は変わらない)を周辺の [min, max] 内で寒色→暖色に色分けする。値が無ければ中立色、
// レンジが潰れている（点が1件 / 全点同値）場合はランプ中央色を返す。
export function priceHeatColor(
  value: number | null | undefined,
  min: number,
  max: number,
): string {
  if (value == null || !Number.isFinite(value)) return PRICE_NEUTRAL_COLOR;
  const n = PRICE_HEAT_COLORS.length;
  if (!(max > min)) return PRICE_HEAT_COLORS[(n - 1) >> 1];
  const t = Math.min(1, Math.max(0, (value - min) / (max - min)));
  return PRICE_HEAT_COLORS[Math.min(n - 1, Math.round(t * (n - 1)))];
}

// 将来推計人口の増減率(%) → 見通し章の正準バンド(メッシュコロプレス配色・StatusRow・総評 Tier が共有)。
// しきい値は全国平均(2020→2050 で約−17%)を基準に「ゆるやかに減る」を広めに取る。
// 朱系の多用はブランド規律(強調5%以下)に反するため、警告色は大きな減少に限定する。
// ※境界(+5/−5/−25)を変えるときは prospects.ts の classifyProspectTier と
//   backend reinfolib.py の _prospects_metrics も同じ値に揃えること(同一数値への形容が章内で矛盾しないように)。
export type PopTrendBand = { min: number; color: string; label: string; shortLabel: string };

export const POP_TREND_BANDS: readonly PopTrendBand[] = [
  { min: 5, color: '#4F8A53', label: '増える見込み (+5%超)', shortLabel: '増える見込み' },
  { min: -5, color: '#92B662', label: 'おおむね横ばい (±5%)', shortLabel: 'おおむね横ばい' },
  { min: -25, color: '#D9A441', label: 'ゆるやかに減る (−25〜−5%)', shortLabel: 'ゆるやかに減る' },
  { min: -Infinity, color: '#C76D5A', label: '大きく減る (−25%超)', shortLabel: '大きく減る' },
] as const;

// 増減率が取得できなかったメッシュ（秘匿・合算など）の中立色。
export const POP_TREND_NEUTRAL_COLOR = '#A8A8A8';

export function popTrendBand(changePct: number | null | undefined): PopTrendBand | null {
  if (changePct == null || !Number.isFinite(changePct)) return null;
  return POP_TREND_BANDS.find((b) => changePct > b.min) ?? null;
}

export function popTrendColor(changePct: number | null | undefined): string {
  return popTrendBand(changePct)?.color ?? POP_TREND_NEUTRAL_COLOR;
}

// 立地適正化計画（居住誘導区域）— 見通し章「まちのかたち」サブマップの塗り色（藍系）。
export const INDUCEMENT_ZONE_COLOR = '#4E7FA8';

// DID（人口集中地区）— 見通し章ではメッシュ塗りと干渉しないよう破線アウトラインで重ねる。
export const DID_OUTLINE_COLOR = '#9C7CB0';

// 用途地域 zone groups — 建築章 map overlays.
// Each 用途地域 code maps to one of 4 visual groups so the legend stays readable.
export type ZoningGroup = 'residential' | 'commercial' | 'industrial' | 'other';

export const ZONING_GROUP_COLOR: Record<ZoningGroup, string> = {
  residential: '#92B662', // 緑系
  commercial: '#D9A441',  // 黄系
  industrial: '#9C7CB0',  // 紫系
  other: '#A8A8A8',
};

export function classifyZoning(label: string | null | undefined): ZoningGroup {
  if (!label) return 'other';
  if (/住居|住宅/.test(label)) return 'residential';
  if (/商業|近隣商業/.test(label)) return 'commercial';
  if (/工業|準工業/.test(label)) return 'industrial';
  return 'other';
}

// 防火・準防火地域 groups — 建築章 map overlays.
export type FireproofGroup = 'strict' | 'quasi' | 'other';

export const FIREPROOF_GROUP_COLOR: Record<FireproofGroup, string> = {
  strict: '#C76D5A', // 防火地域: より厳しい制限
  quasi: '#D9A441',  // 準防火地域: 中程度の制限
  other: '#A8A8A8',
};

export function classifyFireproof(label: string | null | undefined): FireproofGroup {
  if (!label) return 'other';
  // 「準防火」が「防火」の部分文字列であるため、先に「準防火」を判定する。
  if (/準防火/.test(label)) return 'quasi';
  if (/防火地域/.test(label)) return 'strict';
  return 'other';
}
